/**
 * pojistovna-section-forms — the insurer form body, shared by the full-page create surface (`PojistovnaForm`) and the edit modal
 * (`PojistovnaEditModal`), like `tarif-section-forms`. An insurer has a name, a day threshold and, per TARIF of the firma, two daily prices
 * (below / at or above the threshold); an empty price means the insurer does not pay that tarif for that length. Renders
 * `<form id={POJISTOVNA_FORM_ID}>` with fields only; the chrome supplies the submit button through the HTML `form` attribute.
 */

import type { FormEvent } from 'react';
import { Field, Fields } from '@/components/Field';
import { TextInput } from '@/components/TextInput';
import { useSeededSlice, type BodyProps } from '@/modules/vozidla/vozidlo-section-forms';
import type { PojistovnaWriteBody, PujcovnaPojistovna, PujcovnaTarif } from './api';

export const POJISTOVNA_FORM_ID = 'pujcovna-pojistovna-form';

export interface SazbaBox {
  cena1: string;
  cena2: string;
}

export interface PojistovnaValue {
  nazev: string;
  prah_dni: string;
  /** By tarif id. */
  sazby: Record<number, SazbaBox>;
}

const box = (v: number | null | undefined): string => (v == null ? '' : String(v));

/** The form for a stored insurer (or an empty one) over the firma's tarify. */
export function pojistovnaSeed(tarify: ReadonlyArray<Pick<PujcovnaTarif, 'id'>>, p?: PujcovnaPojistovna | null): PojistovnaValue {
  const sazby: Record<number, SazbaBox> = {};
  for (const t of tarify) {
    const s = p?.sazby.find((x) => x.id_tarifu === t.id);
    sazby[t.id] = { cena1: box(s?.cena1), cena2: box(s?.cena2) };
  }
  return { nazev: p?.nazev ?? '', prah_dni: p ? String(p.prah_dni) : '0', sazby };
}

type Cena = { ok: true; cena: number | null } | { ok: false; chyba: string };

/** An empty box = not paid; otherwise a whole number of Kč. */
export function cenaZBoxu(text: string, popis: string): Cena {
  const t = text.trim().replace(/\s/g, '');
  if (t === '') return { ok: true, cena: null };
  if (!/^\d+$/.test(t)) return { ok: false, chyba: `${popis}: „${text.trim()}“ není celé číslo Kč.` };
  const n = Number(t);
  if (n > 99_999) return { ok: false, chyba: `${popis}: cena za den je příliš vysoká.` };
  return { ok: true, cena: n };
}

export type PojistovnaBodyVysledek = { ok: true; body: PojistovnaWriteBody & { nazev: string } } | { ok: false; chyba: string };

/**
 * Trimmed. With `original` (an edit) the body is SLICE-ONLY: the threshold only when it differs, and only the tarify whose pair of prices
 * differs from the stored one, so saving a new name never writes back rates someone else changed meanwhile. A price that is not a whole number
 * is named with its tarif.
 */
export function pojistovnaToBody(
  v: PojistovnaValue,
  tarify: ReadonlyArray<Pick<PujcovnaTarif, 'id' | 'nazev'>>,
  original?: PujcovnaPojistovna | null,
): PojistovnaBodyVysledek {
  const prahText = v.prah_dni.trim();
  if (!/^\d+$/.test(prahText) || Number(prahText) > 366) return { ok: false, chyba: 'Práh dní musí být celé číslo od 0 do 366.' };
  const prah = Number(prahText);
  const sazby: Array<{ id_tarifu: number; cena1: number | null; cena2: number | null }> = [];
  for (const t of tarify) {
    const b = v.sazby[t.id] ?? { cena1: '', cena2: '' };
    const c1 = cenaZBoxu(b.cena1, `${t.nazev} — pod prahem`);
    if (!c1.ok) return c1;
    const c2 = cenaZBoxu(b.cena2, `${t.nazev} — od prahu`);
    if (!c2.ok) return c2;
    const was = original?.sazby.find((x) => x.id_tarifu === t.id);
    if (original && (was?.cena1 ?? null) === c1.cena && (was?.cena2 ?? null) === c2.cena) continue;
    if (!original && c1.cena == null && c2.cena == null) continue;
    sazby.push({ id_tarifu: t.id, cena1: c1.cena, cena2: c2.cena });
  }
  const body: PojistovnaWriteBody & { nazev: string } = { nazev: v.nazev.trim() };
  if (!original || original.prah_dni !== prah) body.prah_dni = prah;
  if (sazby.length > 0) body.sazby = sazby;
  return { ok: true, body };
}

export interface PojistovnaBodyProps extends BodyProps<PojistovnaValue> {
  tarify: ReadonlyArray<Pick<PujcovnaTarif, 'id' | 'nazev'>>;
}

export function PojistovnaFormBody({ initial, tarify, busy, onSubmit, onChange }: PojistovnaBodyProps) {
  const { form, set } = useSeededSlice(initial, onChange);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onSubmit?.(form);
  };
  const setSazba = (id: number, k: keyof SazbaBox, v: string) => set('sazby', { ...form.sazby, [id]: { ...(form.sazby[id] ?? { cena1: '', cena2: '' }), [k]: v } });

  return (
    <form id={POJISTOVNA_FORM_ID} onSubmit={submit}>
      <Fields cols={2}>
        <Field label="Pojišťovna" required>
          <TextInput value={form.nazev} maxLength={100} placeholder="Kooperativa pojišťovna, a.s." onChange={(e) => set('nazev', e.currentTarget.value)} />
        </Field>
        <Field label="Práh dní" hint="Od kolika dní platí druhá cena. 0 = jedna (první) cena pro každou délku.">
          <TextInput inputMode="numeric" value={form.prah_dni} maxLength={3} data-testid="pojistovna-prah" onChange={(e) => set('prah_dni', e.currentTarget.value)} />
        </Field>
      </Fields>
      <div className="mt-5 flex flex-col gap-2" data-testid="pojistovna-sazby">
        <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)] gap-3 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          <span>Tarif</span>
          <span>Cena / den pod prahem (Kč)</span>
          <span>Cena / den od prahu (Kč)</span>
        </div>
        {tarify.length === 0 ? (
          <p className="text-[13px] text-ink-2">Pojišťovna se účtuje podle tarifu vozidla — nejdřív založte tarif.</p>
        ) : (
          tarify.map((t) => (
            <div key={t.id} className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)] items-center gap-3">
              <span className="text-[13px] text-ink">{t.nazev}</span>
              <TextInput
                inputMode="numeric"
                aria-label={`${t.nazev} — cena pod prahem`}
                data-testid={`sazba-${t.id}-1`}
                value={form.sazby[t.id]?.cena1 ?? ''}
                onChange={(e) => setSazba(t.id, 'cena1', e.currentTarget.value)}
              />
              <TextInput
                inputMode="numeric"
                aria-label={`${t.nazev} — cena od prahu`}
                data-testid={`sazba-${t.id}-2`}
                value={form.sazby[t.id]?.cena2 ?? ''}
                onChange={(e) => setSazba(t.id, 'cena2', e.currentTarget.value)}
              />
            </div>
          ))
        )}
        <p className="text-[12px] text-ink-3">Prázdná cena = pojišťovna tento tarif (v dané délce) nehradí.</p>
      </div>
    </form>
  );
}
